import { useCallback } from "react";
import Swal from "sweetalert2";
import { brand } from "../../../config/theme";
import { DIALOG_COLORS } from "../../../components/ui";
import {
  chargeMaintenance,
  deleteMaintenance,
  fetchServiceProducts,
  generateMaintenanceReport,
  transitionMaintenance,
} from "../services/maintenanceService";
import type {
  ChargeTender,
  MaintenanceRecord,
  MaintenanceServiceItem,
  MaintenanceStatus,
  ServiceCatalogItem,
} from "../types";
import { fmtMoney } from "../lib";
import { useMxLabels } from "./useMxLabels";

// Escape catalog names before injecting them into the Swal charge dialog HTML.
function escapeHtml(s: string) {
  return String(s ?? "").replace(
    /[&<>"']/g,
    (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c] as string
  );
}

// Acciones sobre una orden (las comparten listas, tablero, seguimiento y el
// panel de detalle): avanzar estado, eliminar, PDF de la orden y cobro POS.
// `onChanged` recarga lo que la pantalla tenga en memoria.
export function useMaintenanceActions(onChanged: () => void | Promise<void>) {
  const L = useMxLabels();
  const { t } = L;

  const transition = useCallback(
    async (row: MaintenanceRecord, next: MaintenanceStatus) => {
      if (next === "cancelado") {
        const res = await Swal.fire({
          title: t("mantenimiento.confirmCancelTitle"),
          text: t("mantenimiento.confirmCancelText"),
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: L.transition("cancelado"),
          cancelButtonText: t("mantenimiento.cancel"),
          confirmButtonColor: DIALOG_COLORS.danger,
        });
        if (!res.isConfirmed) return false;
      }
      try {
        await transitionMaintenance(row.id, next);
        await onChanged();
        return true;
      } catch {
        Swal.fire({ title: t("mantenimiento.transitionError"), icon: "error", confirmButtonColor: brand.primary });
        return false;
      }
    },
    [t, L, onChanged]
  );

  const remove = useCallback(
    async (row: MaintenanceRecord) => {
      const res = await Swal.fire({
        title: t("mantenimiento.confirmDeleteTitle"),
        text: t("mantenimiento.confirmDeleteText"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: t("mantenimiento.delete"),
        cancelButtonText: t("mantenimiento.cancel"),
        confirmButtonColor: DIALOG_COLORS.danger,
      });
      if (!res.isConfirmed) return false;
      await deleteMaintenance(row.id);
      await onChanged();
      return true;
    },
    [t, onChanged]
  );

  const workOrderPdf = useCallback(
    async (row: MaintenanceRecord) => {
      try {
        const { url } = await generateMaintenanceReport({ kind: "workOrder", id: row.id });
        window.open(url, "_blank");
      } catch {
        Swal.fire({ title: t("mantenimiento.reportError"), icon: "error", confirmButtonColor: brand.primary });
      }
    },
    [t]
  );

  const charge = useCallback(
    async (row: MaintenanceRecord) => {
      if (row.workshopMode !== "externo") {
        Swal.fire({ title: t("mantenimiento.charge"), text: t("mantenimiento.chargeOwnMechanic"), icon: "info", confirmButtonColor: brand.primary });
        return;
      }
      if (row.posSaleId && row.paymentStatus === "paid") {
        Swal.fire({ title: t("mantenimiento.charged"), text: t("mantenimiento.alreadyCharged"), icon: "info", confirmButtonColor: brand.primary });
        return;
      }

      // Build the cart from the POS catalog (labor + parts = POS products).
      let products: ServiceCatalogItem[] = [];
      try {
        products = await fetchServiceProducts();
      } catch {
        /* fall back to the order's stored items / description */
      }

      const existing = row.serviceItems ?? [];
      const existingById = new Map(existing.filter((i) => i.productId).map((i) => [i.productId as string, i]));

      const productRows = products
        .map((p) => {
          const sel = existingById.get(p.id);
          const checked = sel ? "checked" : "";
          const qty = sel?.quantity ?? 1;
          const tag = p.type === "product" ? t("mantenimiento.part") : t("mantenimiento.labor");
          return `<div style="display:flex;align-items:center;gap:8px;padding:4px 0;text-align:left">
            <input type="checkbox" class="mx-prod" value="${escapeHtml(p.id)}" ${checked} />
            <span style="flex:1">${escapeHtml(p.name)}
              <small style="opacity:.6">· ${escapeHtml(tag)} · ${escapeHtml(fmtMoney(p.price, L.locale))}</small></span>
            <input type="number" min="1" class="mx-qty" data-id="${escapeHtml(p.id)}"
              value="${qty}" style="width:56px;padding:2px 4px;border:1px solid #ccc;border-radius:6px" />
          </div>`;
        })
        .join("");

      const emptyHint = products.length ? "" : `<p style="opacity:.7;font-size:.85rem">${escapeHtml(t("mantenimiento.chargeNoCatalog"))}</p>`;

      const result = await Swal.fire({
        title: t("mantenimiento.confirmChargeTitle"),
        html: `
        <div style="max-height:240px;overflow:auto;margin-bottom:10px">${productRows}${emptyHint}</div>
        <label style="display:block;text-align:left;font-size:.85rem;margin-bottom:4px">${escapeHtml(t("mantenimiento.paymentMethod"))}</label>
        <select id="mx-tender" class="swal2-select" style="margin:0;width:100%">
          <option value="cash">${escapeHtml(t("mantenimiento.tenderCash"))}</option>
          <option value="card">${escapeHtml(t("mantenimiento.tenderCard"))}</option>
          <option value="link">${escapeHtml(t("mantenimiento.tenderLink"))}</option>
        </select>
        <p style="opacity:.7;font-size:.8rem;margin-top:8px;text-align:left">${escapeHtml(t("mantenimiento.confirmChargeText"))}</p>`,
        showCancelButton: true,
        confirmButtonText: t("mantenimiento.charge"),
        cancelButtonText: t("mantenimiento.cancel"),
        confirmButtonColor: brand.primary,
        focusConfirm: false,
        preConfirm: () => {
          const tender = (document.getElementById("mx-tender") as HTMLSelectElement | null)?.value as ChargeTender | undefined;
          const items: MaintenanceServiceItem[] = [];
          document.querySelectorAll<HTMLInputElement>(".mx-prod:checked").forEach((cb) => {
            const product = products.find((p) => p.id === cb.value);
            if (!product) return;
            const qtyEl = document.querySelector<HTMLInputElement>(`.mx-qty[data-id="${CSS.escape(cb.value)}"]`);
            items.push({
              productId: product.id,
              name: product.name,
              sku: product.sku ?? null,
              type: (product.type as "service" | "product") || "service",
              price: product.price,
              quantity: Math.max(1, Number(qtyEl?.value || 1)),
            });
          });
          return { tender: tender ?? "cash", items };
        },
      });

      if (!result.isConfirmed) return;
      const { tender, items } = result.value as { tender: ChargeTender; items: MaintenanceServiceItem[] };

      try {
        const { url } = await chargeMaintenance(row.id, {
          tender,
          serviceItems: items.length ? items : undefined,
          successUrl: window.location.href,
          cancelUrl: window.location.href,
        });
        await onChanged();
        if (url) {
          window.open(url, "_blank");
          Swal.fire({ title: t("mantenimiento.chargePending"), text: t("mantenimiento.chargePendingText"), icon: "info", confirmButtonColor: brand.primary });
        } else {
          Swal.fire({ title: t("mantenimiento.chargeDone"), icon: "success", confirmButtonColor: brand.primary });
        }
      } catch {
        Swal.fire({ title: t("mantenimiento.chargeError"), icon: "error", confirmButtonColor: brand.primary });
      }
    },
    [t, L.locale, onChanged]
  );

  return { transition, remove, workOrderPdf, charge };
}
